import React, { useState, useEffect } from 'react';
import { 
  Plus, Edit2, Trash2, Eye, Camera, Heart, BarChart3,
  ChevronLeft, ChevronRight, Grid, Maximize, Check
} from 'lucide-react';
import api from '../../utils/api';

const LookbookManagement = () => {
  const [lookbooks, setLookbooks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedLookbook, setSelectedLookbook] = useState(null);

  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    subtitle: '',
    description: '',
    season: 'SS24',
    collection: 'Anime',
    coverImage: '',
    heroVideo: '',
    isShoppable: true,
    isPublished: false,
    isFeatured: false,
    featuredProducts: [],
    spreads: [],
    story: { title: '', content: '', quote: '', quoteAuthor: '' },
    credits: { photographer: '', stylist: '', makeup: '', location: '', creativeDirector: '' },
    models: [],
    metaTitle: '',
    metaDescription: ''
  });

  useEffect(() => {
    fetchLookbooks();
  }, []);

  const fetchLookbooks = async () => {
    try {
      const res = await api.get('/lookbooks/admin/all');
      setLookbooks(res.data.data);
    } catch (error) {
      console.error('Error fetching lookbooks:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (selectedLookbook) {
        await api.put(`/lookbooks/admin/${selectedLookbook._id}`, formData);
      } else {
        await api.post('/lookbooks/admin', formData);
      }
      
      setShowModal(false);
      setSelectedLookbook(null);
      resetForm();
      fetchLookbooks();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to save lookbook');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this lookbook?')) return;
    
    try {
      await api.delete(`/lookbooks/admin/${id}`);
      fetchLookbooks();
    } catch (error) {
      alert('Failed to delete lookbook');
    }
  };

  const handleEdit = (lookbook) => {
    setSelectedLookbook(lookbook);
    setFormData({
      title: lookbook.title,
      slug: lookbook.slug,
      subtitle: lookbook.subtitle || '',
      description: lookbook.description || '',
      season: lookbook.season,
      collection: lookbook.collection,
      coverImage: lookbook.coverImage,
      heroVideo: lookbook.heroVideo || '',
      isShoppable: lookbook.isShoppable,
      isPublished: lookbook.isPublished,
      isFeatured: lookbook.isFeatured,
      featuredProducts: lookbook.featuredProducts || [],
      spreads: lookbook.spreads || [],
      story: lookbook.story || { title: '', content: '', quote: '', quoteAuthor: '' },
      credits: lookbook.credits || { photographer: '', stylist: '', makeup: '', location: '', creativeDirector: '' },
      models: lookbook.models || [],
      metaTitle: lookbook.metaTitle || '',
      metaDescription: lookbook.metaDescription || ''
    });
    setShowModal(true);
  };

  const addSpread = () => {
    setFormData(prev => ({
      ...prev,
      spreads: [...prev.spreads, {
        layout: 'full_bleed',
        images: [{ url: '', alt: '', hotspot: { enabled: false, x: 50, y: 50 } }],
        caption: '',
        credit: '',
        order: prev.spreads.length
      }]
    }));
  };

  const removeSpread = (index) => {
    setFormData(prev => ({
      ...prev,
      spreads: prev.spreads.filter((_, i) => i !== index)
    }));
  };

  const updateSpread = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      spreads: prev.spreads.map((spread, i) => 
        i === index ? { ...spread, [field]: value } : spread
      )
    }));
  };

  const addModel = () => {
    setFormData(prev => ({
      ...prev,
      models: [...prev.models, { name: '', instagram: '', height: '', measurements: '', sizesWorn: [] }]
    }));
  };

  const resetForm = () => {
    setFormData({
      title: '',
      slug: '',
      subtitle: '',
      description: '',
      season: 'SS24',
      collection: 'Anime',
      coverImage: '',
      heroVideo: '',
      isShoppable: true,
      isPublished: false,
      isFeatured: false,
      featuredProducts: [],
      spreads: [],
      story: { title: '', content: '', quote: '', quoteAuthor: '' },
      credits: { photographer: '', stylist: '', makeup: '', location: '', creativeDirector: '' },
      models: [],
      metaTitle: '',
      metaDescription: ''
    });
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-black uppercase tracking-tighter">Lookbook Management</h1>
          <p className="text-gray-600 font-mono text-sm">Create editorial content and shoppable lookbooks</p>
        </div>
        <button
          onClick={() => { resetForm(); setShowModal(true); }}
          className="bg-yellow-400 text-black px-6 py-3 font-black uppercase tracking-wider border-4 border-black shadow-[4px_4px_0px_black] hover:shadow-none hover:translate-x-1 hover:translate-y-1 transition-all flex items-center gap-2"
        >
          <Plus size={20} />
          Create Lookbook
        </button>
      </div>

      {/* Lookbooks Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {lookbooks.map((lb) => (
          <div 
            key={lb._id} 
            className="bg-white border-4 border-black shadow-[8px_8px_0px_rgba(0,0,0,0.1)] overflow-hidden"
          >
            {/* Cover */}
            <div className="relative aspect-[3/4] bg-gray-100">
              {lb.coverImage ? (
                <img 
                  src={lb.coverImage} 
                  alt={lb.title}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-400">
                  <Camera size={48} />
                </div>
              )}
              
              {/* Status Badges */}
              <div className="absolute top-3 left-3 flex flex-col gap-1">
                {!lb.isPublished && (
                  <div className="bg-gray-600 text-white px-2 py-1 font-bold uppercase text-xs">
                    Draft
                  </div>
                )}
                {lb.isFeatured && (
                  <div className="bg-yellow-400 text-black px-2 py-1 font-bold uppercase text-xs">
                    Featured
                  </div>
                )}
              </div>

              {/* Season Badge */}
              <div className="absolute bottom-3 right-3 bg-black text-white px-3 py-1 font-bold text-xs">
                {lb.season}
              </div>
            </div>

            {/* Content */}
            <div className="p-4">
              <div className="flex justify-between items-start mb-2">
                <h3 className="font-black text-lg uppercase tracking-tighter">{lb.title}</h3>
                {lb.isShoppable && (
                  <span className="text-xs bg-green-100 text-green-800 px-2 py-1 font-bold">
                    Shoppable
                  </span>
                )}
              </div>
              
              <p className="text-gray-500 text-xs font-mono mb-3">
                {lb.collection} • {lb.spreads?.length || 0} spreads
              </p>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-2 mb-4 text-center">
                <div className="bg-gray-100 p-2">
                  <Eye size={16} className="mx-auto mb-1 text-gray-600" />
                  <p className="font-black text-sm">{lb.views || 0}</p>
                </div>
                <div className="bg-gray-100 p-2">
                  <Heart size={16} className="mx-auto mb-1 text-gray-600" />
                  <p className="font-black text-sm">{lb.likes || 0}</p>
                </div>
                <div className="bg-gray-100 p-2">
                  <Grid size={16} className="mx-auto mb-1 text-gray-600" />
                  <p className="font-black text-sm">{lb.spreads?.length || 0}</p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2">
                <button
                  onClick={() => handleEdit(lb)}
                  className="flex-1 bg-black text-white py-2 font-bold uppercase text-sm flex items-center justify-center gap-1 hover:bg-gray-800"
                >
                  <Edit2 size={14} />
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(lb._id)}
                  className="bg-red-600 text-white px-3 py-2 hover:bg-red-700"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-4xl max-h-[90vh] overflow-y-auto border-4 border-black shadow-[8px_8px_0px_black]">
            <div className="bg-black text-white p-4 flex justify-between items-center">
              <h2 className="font-black text-xl uppercase">
                {selectedLookbook ? 'Edit Lookbook' : 'Create Lookbook'}
              </h2>
              <button 
                onClick={() => setShowModal(false)}
                className="text-white hover:text-yellow-400"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block font-bold text-sm mb-1">Title *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    className="w-full p-2 border-2 border-black"
                    required
                  />
                </div>
                <div>
                  <label className="block font-bold text-sm mb-1">Slug *</label>
                  <input
                    type="text"
                    value={formData.slug}
                    onChange={(e) => setFormData({...formData, slug: e.target.value})}
                    className="w-full p-2 border-2 border-black"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block font-bold text-sm mb-1">Season *</label>
                  <select
                    value={formData.season}
                    onChange={(e) => setFormData({...formData, season: e.target.value})}
                    className="w-full p-2 border-2 border-black"
                  >
                    {['SS24', 'FW24', 'SS25', 'FW25', 'Permanent'].map(s => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block font-bold text-sm mb-1">Collection</label>
                  <select
                    value={formData.collection}
                    onChange={(e) => setFormData({...formData, collection: e.target.value})}
                    className="w-full p-2 border-2 border-black"
                  >
                    {['Anime', 'Tantra', 'InkSpeak', 'Soul Venture', 'Collab', 'Special'].map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Images */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block font-bold text-sm mb-1">Cover Image URL *</label>
                  <input
                    type="text"
                    value={formData.coverImage}
                    onChange={(e) => setFormData({...formData, coverImage: e.target.value})}
                    className="w-full p-2 border-2 border-black"
                    required
                  />
                </div>
                <div>
                  <label className="block font-bold text-sm mb-1">Hero Video URL</label>
                  <input
                    type="text"
                    value={formData.heroVideo}
                    onChange={(e) => setFormData({...formData, heroVideo: e.target.value})}
                    className="w-full p-2 border-2 border-black"
                  />
                </div>
              </div>

              {/* Settings */}
              <div className="flex gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.isPublished}
                    onChange={(e) => setFormData({...formData, isPublished: e.target.checked})}
                    className="w-5 h-5"
                  />
                  <span className="font-bold text-sm">Published</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.isFeatured}
                    onChange={(e) => setFormData({...formData, isFeatured: e.target.checked})}
                    className="w-5 h-5"
                  />
                  <span className="font-bold text-sm">Featured</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.isShoppable}
                    onChange={(e) => setFormData({...formData, isShoppable: e.target.checked})}
                    className="w-5 h-5"
                  />
                  <span className="font-bold text-sm">Shoppable</span>
                </label>
              </div>

              {/* Story Section */}
              <div className="border-2 border-gray-200 p-4">
                <h3 className="font-black uppercase mb-4">Story / Narrative</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block font-bold text-sm mb-1">Story Title</label>
                    <input
                      type="text"
                      value={formData.story.title}
                      onChange={(e) => setFormData({...formData, story: {...formData.story, title: e.target.value}})}
                      className="w-full p-2 border-2 border-black"
                    />
                  </div>
                  <div>
                    <label className="block font-bold text-sm mb-1">Story Content</label>
                    <textarea
                      value={formData.story.content}
                      onChange={(e) => setFormData({...formData, story: {...formData.story, content: e.target.value}})}
                      className="w-full p-2 border-2 border-black"
                      rows={3}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block font-bold text-sm mb-1">Quote</label>
                      <input
                        type="text"
                        value={formData.story.quote}
                        onChange={(e) => setFormData({...formData, story: {...formData.story, quote: e.target.value}})}
                        className="w-full p-2 border-2 border-black"
                      />
                    </div>
                    <div>
                      <label className="block font-bold text-sm mb-1">Quote Author</label>
                      <input
                        type="text"
                        value={formData.story.quoteAuthor}
                        onChange={(e) => setFormData({...formData, story: {...formData.story, quoteAuthor: e.target.value}})}
                        className="w-full p-2 border-2 border-black"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Spreads */}
              <div className="border-2 border-gray-200 p-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-black uppercase">Spreads ({formData.spreads.length})</h3>
                  <button
                    type="button"
                    onClick={addSpread}
                    className="bg-black text-white px-3 py-1 font-bold text-sm"
                  >
                    + Add Spread
                  </button>
                </div>

                <div className="space-y-4">
                  {formData.spreads.map((spread, index) => (
                    <div key={index} className="bg-gray-50 p-4 border-2 border-gray-300">
                      <div className="flex justify-between items-center mb-3">
                        <span className="font-bold">Spread #{index + 1}</span>
                        <button
                          type="button"
                          onClick={() => removeSpread(index)}
                          className="text-red-600 hover:text-red-800 font-bold text-sm"
                        >
                          Remove
                        </button>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block font-bold text-sm mb-1">Layout</label>
                          <select
                            value={spread.layout}
                            onChange={(e) => updateSpread(index, 'layout', e.target.value)}
                            className="w-full p-2 border-2 border-black"
                          >
                            <option value="full_bleed">Full Bleed</option>
                            <option value="split">Split (2 images)</option>
                            <option value="gallery">Gallery (3 images)</option>
                          </select>
                        </div>
                        <div>
                          <label className="block font-bold text-sm mb-1">Image URL</label>
                          <input
                            type="text"
                            value={spread.images[0]?.url || ''}
                            onChange={(e) => updateSpread(index, 'images', [{ ...spread.images[0], url: e.target.value }])}
                            className="w-full p-2 border-2 border-black"
                          />
                        </div>
                      </div>

                      <div className="mt-3">
                        <label className="block font-bold text-sm mb-1">Caption</label>
                        <input
                          type="text"
                          value={spread.caption}
                          onChange={(e) => updateSpread(index, 'caption', e.target.value)}
                          className="w-full p-2 border-2 border-black"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Credits */}
              <div className="border-2 border-gray-200 p-4">
                <h3 className="font-black uppercase mb-4">Credits</h3>
                <div className="grid grid-cols-2 gap-4">
                  {Object.keys(formData.credits).map((field) => (
                    <div key={field}>
                      <label className="block font-bold text-sm mb-1 capitalize">
                        {field.replace(/([A-Z])/g, ' $1').trim()}
                      </label>
                      <input
                        type="text"
                        value={formData.credits[field]}
                        onChange={(e) => setFormData({
                          ...formData, 
                          credits: {...formData.credits, [field]: e.target.value}
                        })}
                        className="w-full p-2 border-2 border-black"
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t-2 border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 bg-gray-200 text-black py-3 font-bold uppercase"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-yellow-400 text-black py-3 font-black uppercase tracking-wider border-4 border-black shadow-[4px_4px_0px_black] hover:shadow-none transition-all disabled:opacity-50"
                >
                  {loading ? 'Saving...' : (selectedLookbook ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default LookbookManagement;
